import { DateTime } from './vendor-luxon.mjs';

const WAREHOUSE_TIMEZONE = 'America/Los_Angeles';

const STATIC_SELECTORS = {
  textHolder: '[data-estimate-delivery-date-text]',
}
class EstimateDeliveryDate extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  timer = null;

  /** @type {number} */
  cutoffHour = NaN;

  /** @type {number} */
  workingDaysToDeliver = NaN;

  /** @type {string} */
  orderWithinText = '';

  /** @type {DateTimeInstance} */
  shipDate;

  /** @type {DateTimeInstance} */
  cutOffDate;

  /** @type {HTMLElement | null} */
  textHolder = null;

  constructor() {
    super();
    this.timer = null;
  }

  // Runs when the element is added to the page: reads settings, works out the
  // cutoff time, and starts a 1-second timer to update the countdown.
  /** @returns {void} */
  connectedCallback() {

    this.cutoffHour = Number(this.dataset.cutoffHour);
    this.workingDaysToDeliver = Number(this.dataset.workingDaysToDeliver);
    this.orderWithinText = this.dataset.orderWithinText || '';

    this.shipDate = this.getShipDate();
    this.cutOffDate = this.getCutoffTime(this.shipDate);

    this.textHolder = this.querySelector(STATIC_SELECTORS.textHolder);
    if (!this.textHolder || !this.workingDaysToDeliver || !this.orderWithinText) {
      return
    }

    this.renderCountdown();
    this.timer = setInterval(() => this.renderCountdown(), 1000);

    // Orders before cutoff hour are fulfilled and shipped the same business day.
    // Orders after cutoff hour are fulfilled and shipped the next business morning.
    // ShipMonk Standard delivery: 2–5 business days after fulfillment.
    // Maximum expected delivery window: 5 business days.
    // Shipments occur Monday–Friday only.
    // Exclude December 25 and January 1 from delivery calculations.
  }

  // Returns the day the order ships: today if before cutoff on a shipping day,
  // otherwise the next shipping day.
  /** @returns {DateTimeInstance} The start of the day the order will ship. */
  getShipDate() {
    const now = DateTime.now().setZone(WAREHOUSE_TIMEZONE);
    let shipDay = now.startOf('day');
    const todaysCutoff = shipDay.set({ hour: this.cutoffHour, minute: 0, second: 0, millisecond: 0 });

    if (!this.isShippingDay(shipDay) || now >= todaysCutoff) {
      do {
        shipDay = shipDay.plus({ days: 1 }).startOf('day');
      } while (!this.isShippingDay(shipDay));
    }

    return shipDay;
  }

  // Returns the order cutoff for a ship date (last moment to order for that ship day).
  /**
   * @param {DateTimeInstance} [shipDate] The ship date to derive the cutoff from.
   * @returns {DateTimeInstance} The cutoff moment for the given ship date.
   */
  getCutoffTime(shipDate = this.getShipDate()) {
    return shipDate.set({ hour: this.cutoffHour, minute: 0, second: 0, millisecond: 0 });
  }

  // True if the given date falls on a weekday (Monday–Friday).
  /**
   * @param {DateTimeInstance} dateTime The date to check.
   * @returns {boolean}
   */
  isBusinessDay(dateTime) {
    return dateTime.weekday >= 1 && dateTime.weekday <= 5;
  }

  // True if the warehouse ships on the given date: a weekday that isn't a holiday.
  /**
   * @param {DateTimeInstance} dateTime The date to check.
   * @returns {boolean}
   */
  isShippingDay(dateTime) {
    return this.isBusinessDay(dateTime) && !this.isHoliday(dateTime);
  }

  // True if the given date is a shipping holiday (Dec 25 or Jan 1).
  /**
   * @param {DateTimeInstance} dateTime The date to check.
   * @returns {boolean}
   */
  isHoliday(dateTime) {
    const isHoliday = (dateTime.month === 12 && dateTime.day === 25) || (dateTime.month === 1 && dateTime.day === 1);


    return isHoliday;
  }

  // Adds a number of business days to a date, skipping weekends and holidays.
  /**
   * @param {DateTimeInstance} dateTime The starting date.
   * @param {number} days The number of business days to add.
   * @returns {DateTimeInstance} The resulting date after adding business days.
   */
  addBusinessDays(dateTime, days) {
    let result = dateTime;
    let added = 0;

    while (added < days) {
      result = result.plus({ days: 1 });
      if (this.isShippingDay(result)) {
        added += 1;
      }
    }

    return result;
  }

  // Updates the displayed text every tick: shows the time left until cutoff and
  // the estimated delivery date, or a message once the cutoff has passed.
  /** @returns {void} */
  renderCountdown() {
    const now = DateTime.now();
    const diffMs = this.cutOffDate.toMillis() - now.toMillis();
    const deliveryDate = this.addBusinessDays(this.shipDate, this.workingDaysToDeliver);

    if (diffMs <= 0) {
      // Cutoff passed: roll to the next shipping window and keep counting down.
      this.shipDate = this.getShipDate();
      this.cutOffDate = this.getCutoffTime(this.shipDate);
      return;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const hoursUntilCutOff = Math.floor(totalSeconds / 3600);
    const minutesUntilCutOff = Math.floor((totalSeconds % 3600) / 60);
    const secondsUntilCutOff = totalSeconds % 60;
    const timeUntilCutOff = `${hoursUntilCutOff}h ${minutesUntilCutOff}m ${secondsUntilCutOff}s`;

    const deliveryDateFormatted = deliveryDate.toFormat('EEEE, MMMM d');

    const renderedText = this.orderWithinText
      .replaceAll('[time_countdown]', timeUntilCutOff)
      .replaceAll('[delivery_date]', deliveryDateFormatted);


    this.textHolder.innerHTML = renderedText;
  }

  // Runs when the element is removed from the page: stops the countdown timer.
  /** @returns {void} */
  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('estimate-delivery-date')) {
  customElements.define('estimate-delivery-date', EstimateDeliveryDate);
}
